import React, { useState } from 'react';
import { FileImage, Download } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, ToolActionButtons } from '../shared';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

// Set up PDF.js worker
GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PDFtoJPG = () => {
    const [file, setFile] = useState(null);
    const [totalPages, setTotalPages] = useState(0);
    const [selectedPages, setSelectedPages] = useState([]);
    const [quality, setQuality] = useState(0.92);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const loadingTask = getDocument({ data: arrayBuffer });
                const pdf = await loadingTask.promise;

                setTotalPages(pdf.numPages);
                setSelectedPages(Array.from({ length: pdf.numPages }, (_, i) => i + 1));

                toast.success(`PDF loaded: ${pdf.numPages} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const togglePageSelection = (pageNum) => {
        setSelectedPages(prev => {
            if (prev.includes(pageNum)) {
                return prev.filter(p => p !== pageNum);
            } else {
                return [...prev, pageNum].sort((a, b) => a - b);
            }
        });
    };

    const selectAllPages = () => {
        setSelectedPages(Array.from({ length: totalPages }, (_, i) => i + 1));
    };

    const deselectAllPages = () => {
        setSelectedPages([]);
    };

    const convertToImages = async () => {
        if (selectedPages.length === 0) {
            toast.warning('Please select at least one page');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(0);

            const arrayBuffer = await file.arrayBuffer();
            const loadingTask = getDocument({ data: arrayBuffer });
            const pdf = await loadingTask.promise;

            setProgress(10);

            const zip = new JSZip();

            for (let i = 0; i < selectedPages.length; i++) {
                const pageNum = selectedPages[i];
                setProgress(10 + ((i + 1) / selectedPages.length) * 85);

                const page = await pdf.getPage(pageNum);
                const viewport = page.getViewport({ scale: 2.0 }); // Higher scale for better quality

                // Create canvas
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                // Render PDF page to canvas
                await page.render({
                    canvasContext: context,
                    viewport: viewport
                }).promise;

                // Convert canvas to blob
                const blob = await new Promise(resolve => {
                    canvas.toBlob(resolve, 'image/jpeg', quality);
                });

                // Add to ZIP
                zip.file(`page-${pageNum}.jpg`, blob);
            }

            setProgress(95);

            // Generate ZIP file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            setProgress(100);

            const timestamp = new Date().toISOString().slice(0, 10);

            if (selectedPages.length === 1) {
                // Single image - download directly
                const blob = await zip.file(`page-${selectedPages[0]}.jpg`).async('blob');
                saveAs(blob, `page-${selectedPages[0]}.jpg`);
            } else {
                // Multiple images - download as ZIP
                saveAs(zipBlob, `pdf-to-images-${timestamp}.zip`);
            }

            toast.success(`${selectedPages.length} page(s) converted to images!`);

            setTimeout(() => {
                setProgress(0);
            }, 1000);

        } catch (error) {
            console.error('Conversion error:', error);
            toast.error(`Failed to convert PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            {/* Header */}
            <div className="tool-header">
                <h1 className="tool-title">
                    <FileImage size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    PDF to JPG
                </h1>
                <p className="tool-description">
                    Convert PDF pages to JPG images. Select specific pages or convert all pages at once.
                </p>
            </div>

            {/* File Upload */}
            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileImage size={16} />
                            <span>{file.name}</span>
                            <Badge variant="info">{totalPages} pages</Badge>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                                setFile(null);
                                setTotalPages(0);
                                setSelectedPages([]);
                            }}
                        >
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    {/* Quality Settings */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{
                                fontSize: 'var(--text-xl)',
                                fontWeight: 'var(--font-bold)',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                Image Quality
                            </h3>

                            <div>
                                <label style={{
                                    display: 'block',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-medium)',
                                    marginBottom: 'var(--spacing-2)',
                                    color: 'var(--color-text-secondary)'
                                }}>
                                    Quality: {Math.round(quality * 100)}%
                                </label>
                                <input
                                    type="range"
                                    min="0.5"
                                    max="1"
                                    step="0.01"
                                    value={quality}
                                    onChange={(e) => setQuality(parseFloat(e.target.value))}
                                    style={{ width: '100%' }}
                                />
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    marginTop: 'var(--spacing-1)'
                                }}>
                                    <span>Lower quality (smaller file)</span>
                                    <span>Higher quality (larger file)</span>
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Page Selection */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 'var(--spacing-4)'
                            }}>
                                <h3 style={{
                                    fontSize: 'var(--text-xl)',
                                    fontWeight: 'var(--font-bold)'
                                }}>
                                    Select Pages
                                </h3>
                                <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                                    <Button variant="ghost" size="sm" onClick={selectAllPages}>
                                        Select All
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={deselectAllPages}>
                                        Deselect All
                                    </Button>
                                </div>
                            </div>

                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                                gap: 'var(--spacing-3)'
                            }}>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                                    <div
                                        key={pageNum}
                                        onClick={() => togglePageSelection(pageNum)}
                                        style={{
                                            aspectRatio: '1',
                                            border: selectedPages.includes(pageNum)
                                                ? '2px solid var(--color-accent)'
                                                : '1px solid var(--color-border)',
                                            borderRadius: 'var(--radius-md)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            cursor: 'pointer',
                                            backgroundColor: selectedPages.includes(pageNum)
                                                ? 'var(--color-accent-subtle)'
                                                : 'var(--color-surface)',
                                            transition: 'all var(--transition-base)',
                                            fontWeight: 'var(--font-semibold)',
                                            color: selectedPages.includes(pageNum)
                                                ? 'var(--color-accent)'
                                                : 'var(--color-text-secondary)'
                                        }}
                                    >
                                        {pageNum}
                                    </div>
                                ))}
                            </div>

                            {selectedPages.length > 0 && (
                                <Alert variant="info" style={{ marginTop: 'var(--spacing-4)' }}>
                                    {selectedPages.length} page{selectedPages.length !== 1 ? 's' : ''} selected
                                    {selectedPages.length > 1 && ' (will be downloaded as ZIP)'}
                                </Alert>
                            )}
                        </Card>
                    </div>

                    {/* Progress Bar */}
                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    {/* Convert Button */}
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button
                            variant="primary"
                            size="lg"
                            onClick={convertToImages}
                            disabled={isProcessing || selectedPages.length === 0}
                            loading={isProcessing}
                            fullWidth
                        >
                            <Download size={20} />
                            Convert to JPG
                        </Button>
                    </div>
                </>
            )}
        </div>
    );
};

export default PDFtoJPG;
